import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getPGPropertiesDropDown,
  getMasterMealType,
  getFoodPlanPricing,
  getFoodPlanPricingById,
  createFoodPlanPricing,
  updateFoodPlanPricing,
  toggleFoodPlanPricingStatus,
  getTenantFoodAssignment,
  deleteFoodPlanPricing,
} from "../../../services/propertyOwner/propertyOwnerServices";

const INITIAL_ADD_FORM = {
  pgPropertyId: "",
  label: "",
  description: "",
  monthlyRate: "",
  dailyRate: "",
  absenceDiscount: 0,
  mealTypeIds: [],
};

export default function PgFoodManagement() {
  const [dropdown, setDropdown] = useState([]);
  const [selectedPgId, setSelectedPgId] = useState(null);
  const [foodPlans, setFoodPlans] = useState([]);
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [mealTypes, setMealTypes] = useState([]);

  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);

  const [addForm, setAddForm] = useState(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);

  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchDropdown();
    fetchMealTypes();
  }, []);

  useEffect(() => {
    if (selectedPgId) {
      fetchFoodPlans(selectedPgId);
      fetchTenantAssignments(selectedPgId);
    }
  }, [selectedPgId]);

  const fetchDropdown = async () => {
    try {
      const res = await getPGPropertiesDropDown();
      if (res.statusCode === 1 && res.data?.length > 0) {
        setDropdown(res.data);
        setSelectedPgId(res.data[0].pgPropertyId);
      }
    } catch (err) {
      console.log("Failed to fetch dropdown");
    }
  };

  const fetchMealTypes = async () => {
    try {
      const res = await getMasterMealType();
      if (res.statusCode === 1) setMealTypes(res.data ?? []);
    } catch (err) {
      console.log("Failed to fetch meal types");
    }
  };

  const fetchFoodPlans = async (pgPropertyId) => {
    setPlansLoading(true);
    try {
      const res = await getFoodPlanPricing(pgPropertyId);
      if (res.statusCode === 1) setFoodPlans(res.data ?? []);
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch food plans");
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchTenantAssignments = async (pgPropertyId) => {
    setTenantsLoading(true);
    try {
      const res = await getTenantFoodAssignment(pgPropertyId);
      if (res.statusCode === 1) setTenantAssignments(res.data ?? []);
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to fetch tenant assignments",
      );
    } finally {
      setTenantsLoading(false);
    }
  };

  const handlePgChange = (e) => {
    setSelectedPgId(Number(e.target.value));
  };

  const handleToggle = async (id) => {
    try {
      const res = await toggleFoodPlanPricingStatus(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchFoodPlans(selectedPgId);
      }
    } catch (err) {
      toast.error("Failed to toggle status.");
    }
  };

  // ── Add Modal ──
  const openAddModal = () => {
    setAddForm({ ...INITIAL_ADD_FORM, pgPropertyId: selectedPgId ?? "" });
    setAddErrors({});
    setIsOpenAddModal(true);
  };

  const closeAddModal = () => {
    setIsOpenAddModal(false);
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
  };

  const handleAddMealToggle = (id) => {
    setAddForm((prev) => ({
      ...prev,
      mealTypeIds: prev.mealTypeIds.includes(id)
        ? prev.mealTypeIds.filter((m) => m !== id)
        : [...prev.mealTypeIds, id],
    }));
  };

  const validateAdd = () => {
    const e = {};
    if (!addForm.pgPropertyId) e.pgPropertyId = "PG Property is required.";
    if (!addForm.label) e.label = "Plan name is required.";
    if (addForm.mealTypeIds.length === 0)
      e.mealTypeIds = "Select at least one meal type.";
    if (!addForm.monthlyRate) e.monthlyRate = "Monthly rate is required.";
    setAddErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddSubmit = async () => {
    if (!validateAdd()) return;
    setAddLoading(true);
    try {
      const payload = {
        pgPropertyId: Number(addForm.pgPropertyId),
        label: addForm.label,
        description: addForm.description,
        monthlyRate: Number(addForm.monthlyRate),
        dailyRate: Number(addForm.dailyRate) || 0,
        absenceDiscount: Number(addForm.absenceDiscount) || 0,
        mealTypeIds: addForm.mealTypeIds,
      };
      const res = await createFoodPlanPricing(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddModal();
        fetchFoodPlans(selectedPgId);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit Modal ──
  const openEditModal = async (id) => {
    try {
      const res = await getFoodPlanPricingById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedPlan(d);
        const existingMeals = d.mealTypesJson
          ? JSON.parse(d.mealTypesJson)
          : [];
        const matchedIds = mealTypes
          .filter((m) => existingMeals.includes(m.name))
          .map((m) => m.mealTypeId);
        setEditForm({
          label: d.label || "",
          description: d.description || "",
          monthlyRate: d.monthlyRate ?? "",
          dailyRate: d.dailyRate ?? "",
          absenceDiscount: d.absenceDiscount ?? 0,
          mealTypeIds: matchedIds,
        });
        setEditErrors({});
        setIsOpenEditModal(true);
      }
    } catch (err) {
      toast.error("Failed to load food plan details.");
    }
  };

  const closeEditModal = () => {
    setIsOpenEditModal(false);
    setSelectedPlan(null);
    setEditForm({});
    setEditErrors({});
  };

  const handleEditMealToggle = (id) => {
    setEditForm((prev) => ({
      ...prev,
      mealTypeIds: prev.mealTypeIds.includes(id)
        ? prev.mealTypeIds.filter((m) => m !== id)
        : [...prev.mealTypeIds, id],
    }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.label) e.label = "Plan name is required.";
    if (!editForm.mealTypeIds || editForm.mealTypeIds.length === 0)
      e.mealTypeIds = "Select at least one meal type.";
    if (!editForm.monthlyRate) e.monthlyRate = "Monthly rate is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const payload = {
        label: editForm.label,
        description: editForm.description,
        monthlyRate: Number(editForm.monthlyRate),
        dailyRate: Number(editForm.dailyRate) || 0,
        absenceDiscount: Number(editForm.absenceDiscount) || 0,
        mealTypeIds: editForm.mealTypeIds,
      };
      const res = await updateFoodPlanPricing(selectedPlan.foodPlanId, payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditModal();
        fetchFoodPlans(selectedPgId);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };
  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteFoodPlanPricing(deleteTarget.foodPlanId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setDeleteTarget(null);
        fetchFoodPlans(selectedPgId);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Food Management
            </div>
            <div className="page-sub" id="page-sub">
              Settings › Food plan configuration for PG properties
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Food Plan
            </button>
          </div>
        </div>

        <div className="content" id="content-area">
          <div className="flex-between mb14">
            <div />
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <label style={{ fontSize: 12, fontWeight: 600 }}>
                PG Property:
              </label>
              <select
                style={{ fontSize: 12, padding: "5px 8px", width: "auto" }}
                value={selectedPgId ?? ""}
                onChange={handlePgChange}
              >
                {dropdown.map((d) => (
                  <option key={d.pgPropertyId} value={d.pgPropertyId}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="two-col">
            {/* Food Plans */}
            <div className="card">
              <div className="card-title">Food Plans &amp; Pricing</div>
              <div className="info-box mb10" style={{ fontSize: 11 }}>
                Each food plan can bundle multiple meal types (e.g. Lunch +
                Dinner) at a single combined price.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Plan Name</th>
                      <th>Includes</th>
                      <th>Monthly</th>
                      <th>Daily</th>
                      <th>Absence Disc.</th>
                      <th>Subscribers</th>
                      <th>Enabled</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plansLoading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 8 }).map((_, j) => (
                            <td key={j}>
                              <div
                                style={{
                                  height: 14,
                                  borderRadius: 6,
                                  background:
                                    "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                  backgroundSize: "200% 100%",
                                  animation: "shimmer 1.4s infinite",
                                  width: "70%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : foodPlans.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          style={{
                            textAlign: "center",
                            color: "var(--text-tertiary)",
                            padding: 24,
                          }}
                        >
                          No food plans found.
                        </td>
                      </tr>
                    ) : (
                      foodPlans.map((plan) => {
                        // const mealTypesParsed = plan.mealTypesJson
                        //   ? JSON.parse(plan.mealTypesJson)
                        //   : [];
                        return (
                          <tr key={plan.foodPlanId}>
                            <td>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {plan.label}
                              </div>
                              {plan.description && (
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: "var(--text-tertiary)",
                                  }}
                                >
                                  {plan.description}
                                </div>
                              )}
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 3,
                                }}
                              >
                                {(plan.mealTypeNames
                                  ? plan.mealTypeNames.split(",")
                                  : []
                                ).map((meal, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      background: "var(--teal-light)",
                                      color: "var(--teal)",
                                      borderRadius: 99,
                                      padding: "1px 7px",
                                      fontSize: 10,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {meal.trim()}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td style={{ fontWeight: 600 }}>
                              ₹{plan.monthlyRate?.toLocaleString("en-IN")}
                            </td>
                            <td style={{ fontWeight: 600 }}>
                              ₹{plan.dailyRate?.toLocaleString("en-IN")}
                            </td>
                            <td>
                              {plan.absenceDiscount > 0 ? (
                                <span className="badge badge-info">
                                  ₹{plan.absenceDiscount}/day
                                </span>
                              ) : (
                                <span className="badge badge-gray">None</span>
                              )}
                            </td>
                            <td>
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: "var(--teal)",
                                }}
                              >
                                {plan.subscribers} tenants
                              </span>
                            </td>
                            <td>
                              <label
                                className="toggle"
                                style={{ display: "inline-block" }}
                              >
                                <input
                                  type="checkbox"
                                  checked={plan.isActive}
                                  onChange={() => handleToggle(plan.foodPlanId)}
                                />
                                <span className="toggle-slider" />
                              </label>
                            </td>
                            <td>
                              <div className="td-actions">
                                <span
                                  className="action-icon"
                                  onClick={() => openEditModal(plan.foodPlanId)}
                                >
                                  <span className="tip">Edit</span>✎
                                </span>
                                <span
                                  className="action-icon red"
                                  onClick={() => setDeleteTarget(plan)}
                                >
                                  <span className="tip">Delete</span>🗑
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tenant Food Assignments */}
            <div className="card">
              <div className="card-title">Tenant Food Assignments</div>
              <div className="info-box mb10" style={{ fontSize: 11 }}>
                Select a food plan for each tenant. The plan cost is added to
                their monthly bill.
              </div>
              <div style={{ overflowX: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Assigned Plan</th>
                      <th>Monthly Food Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantsLoading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 3 }).map((_, j) => (
                            <td key={j}>
                              <div
                                style={{
                                  height: 14,
                                  borderRadius: 6,
                                  background:
                                    "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                  backgroundSize: "200% 100%",
                                  animation: "shimmer 1.4s infinite",
                                  width: "70%",
                                }}
                              />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : tenantAssignments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          style={{
                            textAlign: "center",
                            color: "var(--text-tertiary)",
                            padding: 24,
                          }}
                        >
                          No tenants found.
                        </td>
                      </tr>
                    ) : (
                      tenantAssignments.map((t) => (
                        <tr key={t.tenantId}>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 12 }}>
                              {t.fullName}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: "var(--text-secondary)",
                              }}
                            >
                              📱 {t.mobile}
                            </div>
                          </td>
                          <td>
                            <select
                              value={t.foodPlanId ?? ""}
                              onChange={() => {}}
                              style={{
                                fontSize: 12,
                                padding: "4px 7px",
                                border: "0.5px solid var(--border-mid)",
                                borderRadius: 6,
                                minWidth: 180,
                              }}
                            >
                              <option value="">— No Food Plan —</option>
                              {foodPlans.map((plan) => (
                                <option
                                  key={plan.foodPlanId}
                                  value={plan.foodPlanId}
                                >
                                  {plan.label} (₹
                                  {plan.monthlyRate?.toLocaleString("en-IN")}
                                  /mo)
                                </option>
                              ))}
                            </select>
                          </td>
                          <td style={{ fontWeight: 600, color: "var(--teal)" }}>
                            ₹{t.monthlyFoodCost?.toLocaleString("en-IN")}/mo
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      {isOpenAddModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 750, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Add Food Plan</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  Bundle one or more meal types at a single price
                </div>
              </div>
              <button className="modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb14">
                <label>PG Property *</label>
                <select
                  value={addForm.pgPropertyId}
                  onChange={(e) => {
                    setAddForm((p) => ({ ...p, pgPropertyId: e.target.value }));
                    setAddErrors((p) => ({ ...p, pgPropertyId: "" }));
                  }}
                  style={
                    addErrors.pgPropertyId ? { borderColor: "#dc2626" } : {}
                  }
                >
                  <option value="">-- Select PG Property --</option>
                  {dropdown.map((d) => (
                    <option key={d.pgPropertyId} value={d.pgPropertyId}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {addErrors.pgPropertyId && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addErrors.pgPropertyId}
                  </span>
                )}
              </div>

              <div className="form-group mb14">
                <label>Plan Name / Label *</label>
                <input
                  type="text"
                  value={addForm.label}
                  onChange={(e) => {
                    setAddForm((p) => ({ ...p, label: e.target.value }));
                    setAddErrors((p) => ({ ...p, label: "" }));
                  }}
                  placeholder="e.g. Lunch + Dinner, All Meals"
                  style={addErrors.label ? { borderColor: "#dc2626" } : {}}
                />
                {addErrors.label && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addErrors.label}
                  </span>
                )}
              </div>

              <div className="form-group mb14">
                <label>Description</label>
                <input
                  type="text"
                  value={addForm.description}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Brief description"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  Meal Types Included *{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (select all that apply)
                  </span>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 7,
                  }}
                >
                  {mealTypes.map((m) => (
                    <label
                      key={m.mealTypeId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 500,
                        background: addForm.mealTypeIds.includes(m.mealTypeId)
                          ? "var(--teal-light)"
                          : "#fff",
                        border: `1px solid ${addForm.mealTypeIds.includes(m.mealTypeId) ? "var(--teal)" : "#e5e7eb"}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addForm.mealTypeIds.includes(m.mealTypeId)}
                        onChange={() => {
                          handleAddMealToggle(m.mealTypeId);
                          setAddErrors((p) => ({ ...p, mealTypeIds: "" }));
                        }}
                        style={{ width: "auto", accentColor: "var(--teal)" }}
                      />
                      {m.name}
                    </label>
                  ))}
                </div>
                {addErrors.mealTypeIds && (
                  <span
                    style={{
                      color: "#dc2626",
                      fontSize: 11,
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    {addErrors.mealTypeIds}
                  </span>
                )}
                <div className="input-hint" style={{ marginTop: 6 }}>
                  Selected meals are bundled into one plan at the prices set
                  below.
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10,
                  padding: "8px 12px",
                  background: "#f8fafc",
                  borderRadius: "8px 8px 0 0",
                  border: "0.5px solid #e5e7eb",
                  borderBottom: "none",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Monthly Rate
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Daily Rate
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Absence Discount{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (₹/day off)
                  </span>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10,
                  padding: 12,
                  border: "0.5px solid #e5e7eb",
                  borderRadius: "0 0 8px 8px",
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                  <input
                    type="number"
                    value={addForm.monthlyRate}
                    onChange={(e) => {
                      setAddForm((p) => ({
                        ...p,
                        monthlyRate: e.target.value,
                      }));
                      setAddErrors((p) => ({ ...p, monthlyRate: "" }));
                    }}
                    placeholder="e.g. 1500"
                    min={0}
                    style={{
                      flex: 1,
                      ...(addErrors.monthlyRate
                        ? { borderColor: "#dc2626" }
                        : {}),
                    }}
                  />
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>/mo</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                  <input
                    type="number"
                    value={addForm.dailyRate}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, dailyRate: e.target.value }))
                    }
                    placeholder="e.g. 60"
                    min={0}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>/day</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                  <input
                    type="number"
                    value={addForm.absenceDiscount}
                    onChange={(e) =>
                      setAddForm((p) => ({
                        ...p,
                        absenceDiscount: e.target.value,
                      }))
                    }
                    placeholder="e.g. 50"
                    min={0}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>/day</span>
                </div>
              </div>
              {addErrors.monthlyRate && (
                <span style={{ color: "#dc2626", fontSize: 11 }}>
                  {addErrors.monthlyRate}
                </span>
              )}
              <div className="input-hint">
                Absence discount: deducted per day when tenant marks a meal
                absence. Leave 0 for no discount.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModal}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "💾 Save Food Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {isOpenEditModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 750, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  Edit Food Plan — {selectedPlan?.label}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  Bundle one or more meal types at a single price
                </div>
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb14">
                <label>Plan Name / Label *</label>
                <input
                  type="text"
                  value={editForm.label}
                  onChange={(e) => {
                    setEditForm((p) => ({ ...p, label: e.target.value }));
                    setEditErrors((p) => ({ ...p, label: "" }));
                  }}
                  placeholder="e.g. Lunch + Dinner, All Meals"
                  style={editErrors.label ? { borderColor: "#dc2626" } : {}}
                />
                {editErrors.label && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {editErrors.label}
                  </span>
                )}
              </div>

              <div className="form-group mb14">
                <label>Description</label>
                <input
                  type="text"
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, description: e.target.value }))
                  }
                  placeholder="Brief description"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 8,
                  }}
                >
                  Meal Types Included *{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (select all that apply)
                  </span>
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 7,
                  }}
                >
                  {mealTypes.map((m) => (
                    <label
                      key={m.mealTypeId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 500,
                        background: editForm.mealTypeIds?.includes(m.mealTypeId)
                          ? "var(--teal-light)"
                          : "#fff",
                        border: `1px solid ${editForm.mealTypeIds?.includes(m.mealTypeId) ? "var(--teal)" : "#e5e7eb"}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          editForm.mealTypeIds?.includes(m.mealTypeId) || false
                        }
                        onChange={() => {
                          handleEditMealToggle(m.mealTypeId);
                          setEditErrors((p) => ({ ...p, mealTypeIds: "" }));
                        }}
                        style={{ width: "auto", accentColor: "var(--teal)" }}
                      />
                      {m.name}
                    </label>
                  ))}
                </div>
                {editErrors.mealTypeIds && (
                  <span
                    style={{
                      color: "#dc2626",
                      fontSize: 11,
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    {editErrors.mealTypeIds}
                  </span>
                )}
                <div className="input-hint" style={{ marginTop: 6 }}>
                  Selected meals are bundled into one plan at the prices set
                  below.
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10,
                  padding: "8px 12px",
                  background: "#f8fafc",
                  borderRadius: "8px 8px 0 0",
                  border: "0.5px solid #e5e7eb",
                  borderBottom: "none",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Monthly Rate
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Daily Rate
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Absence Discount{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (₹/day off)
                  </span>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10,
                  padding: 12,
                  border: "0.5px solid #e5e7eb",
                  borderRadius: "0 0 8px 8px",
                  marginBottom: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                  <input
                    type="number"
                    value={editForm.monthlyRate}
                    onChange={(e) => {
                      setEditForm((p) => ({
                        ...p,
                        monthlyRate: e.target.value,
                      }));
                      setEditErrors((p) => ({ ...p, monthlyRate: "" }));
                    }}
                    placeholder="e.g. 1500"
                    min={0}
                    style={{
                      flex: 1,
                      ...(editErrors.monthlyRate
                        ? { borderColor: "#dc2626" }
                        : {}),
                    }}
                  />
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>/mo</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                  <input
                    type="number"
                    value={editForm.dailyRate}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, dailyRate: e.target.value }))
                    }
                    placeholder="e.g. 60"
                    min={0}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>/day</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                  <input
                    type="number"
                    value={editForm.absenceDiscount}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        absenceDiscount: e.target.value,
                      }))
                    }
                    placeholder="e.g. 50"
                    min={0}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>/day</span>
                </div>
              </div>
              {editErrors.monthlyRate && (
                <span style={{ color: "#dc2626", fontSize: 11 }}>
                  {editErrors.monthlyRate}
                </span>
              )}
              <div className="input-hint">
                Absence discount: deducted per day when tenant marks a meal
                absence. Leave 0 for no discount.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "💾 Save Food Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Delete Food Plan</div>
              <button
                className="modal-close"
                onClick={() => setDeleteTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 14, marginBottom: 8 }}>
                Are you sure you want to delete{" "}
                <strong>{deleteTarget?.label}</strong>?
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                This action cannot be undone. Tenants assigned to this plan will
                lose their food assignment.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn"
                style={{
                  background: "var(--red, #dc2626)",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "🗑 Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
